import {getType} from 'typesafe-actions';
import {Machine} from 'xstate';
import {ROUTES} from '../../containers/App/Routes';
import {BI_ERRORS} from '../../services/biErrorLogger/biErrors';
import {BiLogger} from '../../services/biLogger/biLogger';
import {
  backButtonAction,
  cashierMandatoryFieldsAction,
  cashierWidgetLoadStateAction,
  closeAction,
  continuePaymentAction,
  downloadLinksSuccessAction,
  emailGotAction,
  emailGotInvalidAction,
  getAlbumAction,
  getTrackAction,
  onNameYourPriceEnteredAction,
  payWithCashierCanceledAction,
  payWithCashierFailureAction,
  payWithCashierPendingAction,
  payWithCashierSuccessAction,
  retryPaymentAction,
  sendBiErrorAction,
} from '../../store/actions';
import {GET_CONTAINER_DISPLAY_M} from '../getContainerDisplay';
import {playerMachine} from '../playerMachine/playerMachine';
import {PlayerMachineContext} from '../playerMachine/PlayerMachineContext';
import {AppContext} from './AppContext';
import {machineOptions} from './machineOptions';

export const playerMachineId = 'playerMachine';

export const createMachine = ({biLogger}: {biLogger: BiLogger}) =>
  Machine<AppContext>(
    {
      id: 'root',
      initial: 'bootstrap',
      invoke: {
        id: playerMachineId,
        src: 'playerMachine',
        data: getMachineData,
      },
      states: {
        bootstrap: {
          id: 'bootstrap',
          on: {
            '': [{target: '#trackInfo', cond: 'isTrackInfoRoute'}, {target: '#mainGet'}],
          },
        },
        trackInfo: {
          id: 'trackInfo',
          initial: 'show',
          onEntry: [{type: 'updateOrigin', origin: 'track_page'}],
          meta: {
            route: ROUTES.TRACK_INFO,
          },
          states: {
            show: {
              on: {
                TRACK_CHANGED: {
                  actions: 'updateTrack',
                },
                [getType(getAlbumAction)]: {
                  target: '#mainGet',
                  actions: 'clearTrack',
                },
                [getType(getTrackAction)]: {
                  target: '#mainGet',
                },
              },
            },
          },
        },
        get: {
          id: 'mainGet',
          strict: true,
          initial: 'initialPaymentResults',
          onEntry: [{type: 'updateOrigin', origin: 'download_page'}],
          meta: {
            route: ROUTES.GET,
          },
          states: {
            initialPaymentResults: {
              on: {
                '': [
                  {target: '#paymentError', cond: 'isInitialTransactionFailure'},
                  {target: '#paymentPending', cond: 'isInitialTransactionCompletedWithSnapshot'},
                  {target: '#paymentCompletedPending', cond: 'isInitialTransactionPending'},
                  {
                    target: '#paymentCompletedSuccessful',
                    cond: 'isInitialTransactionSuccess',
                    actions: 'assignEmptyDownloadLink',
                  },
                  {target: '#getContainer'},
                ],
              },
            },
            getContainer: {
              id: 'getContainer',
              initial: 'checkAlbum',
              states: {
                checkAlbum: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.LOADING},
                  invoke: {
                    src: 'checkAlbumDownloadStatus',
                    onDone: [
                      {target: 'init', cond: 'canDownloadAlbum'},
                      {target: 'entityNotGettable', actions: 'logNotReadyAlbum'},
                    ],
                    onError: {
                      target: '#genericError',
                      actions: ['storeError', sendBiErrorAction(BI_ERRORS.getWindow.dgs.checkZipStatus)],
                    },
                  },
                },
                entityNotGettable: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.ENTITY_NOT_GETTABLE},
                },
                init: {
                  on: {
                    '': [
                      {target: 'demo', cond: 'isDemoFlow'},
                      {target: 'buy', cond: 'isBuyFlow'},
                      {target: 'free', cond: 'isFreeFlow'},
                      {target: 'subscribe', cond: 'isSubscribeFlow'},
                      {target: 'nameYourPrice', cond: 'isNameYourPriceFlow'},
                      {target: 'noPurchase', cond: 'isNoneFlow'},
                    ],
                  },
                },
                demo: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.DEMO_ALBUM},
                },
                noPurchase: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.NO_PURCHASE_AVAILABLE},
                },
                buy: {
                  initial: 'startPurchase',
                  states: {
                    startPurchase: {
                      id: 'startPurchase',
                      meta: {show: GET_CONTAINER_DISPLAY_M.START_PURCHASE},
                      on: {
                        [getType(continuePaymentAction)]: 'getEmail',
                        [getType(closeAction)]: {actions: 'closeModal'},
                      },
                    },
                    ...paymentStates({prefix: 'buy', backState: '#startPurchase'}),
                  },
                },
                free: {
                  id: 'freeFlow',
                  initial: 'init',
                  states: {
                    init: {
                      on: {'': [{target: '#subscribeFlow', cond: 'isIos'}, {target: 'loading'}]},
                    },
                    loading: {
                      meta: {show: GET_CONTAINER_DISPLAY_M.LOADING},
                      invoke: {
                        src: 'getFreeDownloadLinks',
                        onDone: {
                          target: 'links',
                          actions: 'storeDownloadLink',
                        },
                        onError: {
                          target: '#genericError',
                          actions: 'storeError',
                        },
                      },
                    },
                    links: {
                      meta: {show: GET_CONTAINER_DISPLAY_M.FREE_DOWNLOAD_LINKS},
                      on: {
                        [getType(closeAction)]: {actions: 'closeModal'},
                      },
                    },
                  },
                },
                subscribe: {
                  id: 'subscribeFlow',
                  ...subscribeStates(),
                },
                nameYourPrice: {
                  initial: 'getPrice',
                  states: {
                    getPrice: {
                      id: 'getPrice',
                      meta: {show: GET_CONTAINER_DISPLAY_M.NAME_YOUR_PRICE},
                      on: {
                        [getType(onNameYourPriceEnteredAction)]: [
                          {
                            actions: 'storePriceOverride',
                            cond: 'isZeroPriceOverride',
                            target: 'withZero',
                          },
                          {
                            actions: 'storePriceOverride',
                            target: 'getEmail',
                          },
                        ],
                        [getType(closeAction)]: {actions: 'closeModal'},
                      },
                    },
                    withZero: {
                      on: {
                        '': [{target: 'subscribe', cond: 'isSubscriptionRequired'}, {target: '#freeFlow'}],
                      },
                    },
                    subscribe: {
                      ...subscribeStates(GET_CONTAINER_DISPLAY_M.SUBSCRIBE_STANDALONE, '#getPrice'),
                    },

                    ...paymentStates({prefix: 'nameYourPrice', backState: '#getPrice'}),
                  },
                },
              },
            },
            paymentError: {
              id: 'paymentError',
              entry: {type: 'paymentCompletedAnalytics', paymentStatus: 'FAILURE'},
              meta: {show: GET_CONTAINER_DISPLAY_M.PAYMENT_ERROR},
              on: {
                [getType(backButtonAction)]: {target: '#getContainer', actions: 'backActionAnalytics'},
                [getType(closeAction)]: {actions: 'closeModal'},
                [getType(retryPaymentAction)]: {target: '#getContainer', actions: 'retryPaymentAnalytics'},
              },
            },
            paymentPending: {
              id: 'paymentPending',
              initial: 'loading',
              states: {
                loading: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.PROCESSING_PAYMENT},
                  invoke: {
                    src: 'getDownloadLink',
                    onError: {
                      target: 'pending',
                      actions: 'storeError',
                    },
                  },
                  on: {
                    [getType(downloadLinksSuccessAction)]: {
                      target: 'preSuccess',
                      actions: 'storeDownloadLink',
                    },
                  },
                  after: {40000: 'pending'},
                },
                preSuccess: {
                  meta: {show: GET_CONTAINER_DISPLAY_M.PROCESSING_PAYMENT},
                  after: {1000: 'success'},
                },
                success: {
                  id: 'paymentCompletedSuccessful',
                  entry: {type: 'paymentCompletedAnalytics', paymentStatus: 'SUCCESS'},
                  meta: {show: GET_CONTAINER_DISPLAY_M.DOWNLOAD_LINKS},
                  on: {
                    [getType(closeAction)]: {actions: 'closeModal'},
                  },
                },
                pending: {
                  id: 'paymentCompletedPending',
                  entry: {type: 'paymentCompletedAnalytics', paymentStatus: 'PENDING'},
                  meta: {show: GET_CONTAINER_DISPLAY_M.PAYMENT_PENDING},
                },
              },
            },
            genericError: {
              id: 'genericError',
              meta: {show: GET_CONTAINER_DISPLAY_M.ERROR},
              onEntry: 'logError',
            },
          },
        },
      },
    },
    machineOptions({biLogger}),
  );

export function subscribeStates(getEmailShow = GET_CONTAINER_DISPLAY_M.SUBSCRIBE, backState?: string) {
  return {
    initial: 'getEmail',
    states: {
      getEmail: {
        meta: {show: getEmailShow},
        on: {
          ...(backState
            ? {[getType(backButtonAction)]: backState}
            : {[getType(closeAction)]: {actions: ['closeModal', 'backActionAnalytics']}}),
          [getType(emailGotAction)]: {
            target: 'loading',
            actions: ['storeEmail', 'subscribeEmailGotAnalytics'],
          },
          [getType(emailGotInvalidAction)]: {
            actions: ['subscribeEmailGotAnalytics'],
          },
        },
      },
      loading: {
        meta: {show: GET_CONTAINER_DISPLAY_M.LOADING},
        invoke: {
          src: 'subscribeByEmail',
          onDone: {
            target: 'subscribed',
            actions: {type: 'subscribeAnalytics', isSuccess: true},
          },
          onError: {
            target: '#genericError',
            actions: ['storeError', {type: 'subscribeAnalytics', isSuccess: false}],
          },
        },
      },
      subscribed: {
        meta: {show: GET_CONTAINER_DISPLAY_M.DOWNLOAD_LINKS},
      },
    },
  };
}

export function paymentStates({prefix, backState}: {prefix: string; backState: string}) {
  return {
    getEmail: {
      id: `${prefix}_getEmail`,
      meta: {show: GET_CONTAINER_DISPLAY_M.GET_EMAIL_AND_PURCHASE},
      on: {
        [getType(backButtonAction)]: {target: backState, actions: 'backActionAnalytics'},
        [getType(emailGotAction)]: {
          target: 'lazyPaymentWidget',
          actions: ['storeEmail', {type: 'emailGotAnalytics', isSuccess: true}],
        },
        [getType(emailGotInvalidAction)]: {
          actions: {type: 'emailGotAnalytics', isSuccess: false},
        },
      },
    },
    lazyPaymentWidget: {
      initial: 'loading',
      states: {
        loading: {
          meta: {show: GET_CONTAINER_DISPLAY_M.LOADING},
          on: {
            [getType(backButtonAction)]: {target: `#${prefix}_getEmail`, actions: 'backActionAnalytics'},
          },
          invoke: {
            src: 'getSnapshot',
            onDone: {
              target: 'showLazyWidget',
              actions: 'storeSnapshotId',
            },
            onError: {
              target: '#genericError',
              actions: 'storeError',
            },
          },
        },
        showLazyWidget: {
          meta: {show: GET_CONTAINER_DISPLAY_M.CASHIER_LAZY_WIDGET},
          entry: {type: 'reportWidgetLoadStart'},
          on: {
            [getType(backButtonAction)]: {
              target: `#${prefix}_getEmail`,
              actions: 'backActionAnalytics',
            },
            [getType(payWithCashierSuccessAction)]: '#paymentPending',
            [getType(payWithCashierPendingAction)]: '#paymentPending',
            [getType(payWithCashierFailureAction)]: {
              target: '#paymentError',
              actions: 'storePaymentErrorMessage',
            },
            [getType(payWithCashierCanceledAction)]: {
              actions: {type: 'paymentCompletedAnalytics', paymentStatus: 'CANCELED'},
            },
            [getType(cashierWidgetLoadStateAction)]: {
              actions: 'updateCashierWidgetStatus',
            },
            [getType(cashierMandatoryFieldsAction)]: {
              actions: 'storeMandatoryFields',
            },
          },
        },
      },
    },
  };
}

function getMachineData({album, track, player}: AppContext): PlayerMachineContext {
  return {album, track, player};
}
